import type { Workflow } from '../models/common.ts'
import type { GeoPoint } from '../models/geo-point.ts'
import {
  vBoolean,
  vMaxValue,
  vMinValue,
  vObject,
  vOpt,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vStringToNumber,
} from '../validation/index.ts'

export const comparableLoadsWindows = [
  '7-days',
  '14-days',
  '21-days',
  '30-days',
  '60-days',
  '90-days',
] as const

export type ComparableLoadsWindow = (typeof comparableLoadsWindows)[number]

export const getComparableLoadsWindowLabel = (
  window: ComparableLoadsWindow,
) => {
  const days = window.replace('-days', '')
  return `±${days} days`
}

export const comparableLoadsLaneToleranceRadii = [
  5, 10, 25, 50, 75, 100,
] as const

export type ComparableLoadsLaneToleranceRadius =
  (typeof comparableLoadsLaneToleranceRadii)[number]

export const comparableLoadsQuerySchema = vObject({
  bookedWindow: vOptional(vPicklist(comparableLoadsWindows), '30-days'),
  pickupWindow: vOptional(vPicklist(comparableLoadsWindows), '30-days'),
  laneToleranceRadiusMiles: vOpt(
    vPipe(vStringToNumber(), vPicklist(comparableLoadsLaneToleranceRadii)),
    25,
  ),
  limit: vOpt(vPipe(vStringToNumber(), vMinValue(1), vMaxValue(100)), 50),
  includeSampleLoads: vOpt(vBoolean(), true),
})
export type ComparableLoadsOptions = vOutput<typeof comparableLoadsQuerySchema>

export type ComparableLoadStatus = 'booked' | 'quoting'

export interface ComparableLoadSearchProfile {
  originGeo: GeoPoint
  destinationGeo: GeoPoint
  workflow: Workflow
  pickupDateStart: Date
  pickupDateEnd: Date
  bookedDateStart: Date
  bookedDateEnd: Date
}

export interface PriceBenchmark {
  count: number
  average: number | null
  cwtAverage: number | null
}

export interface ComparableLoadSource {
  accountId: string
  cwt: number | null
}

export interface ComparableLoadSummary {
  accountId: string
  loadId: string
  status: ComparableLoadStatus
  amount: number | null
  cheapestQuoteAmount: number | null
  pickupDate: string
  bookedDate: string | null
}

export interface ComparableLoadsResult {
  source: ComparableLoadSource
  market: PriceBenchmark
  account: PriceBenchmark
  samples?: ComparableLoadSummary[]
}
